<template>
  <custom-popup
    v-model="dialog"
    :cons="{
      title: `مدیریت قیمت گروهی (${selectedItems.length} تنوع انتخاب شده)`,
      icon: 'cash-edit'
    }"
    :loading="loading"
    max-width="580"
    hide-confirm
    persistent
    @close="closeModal"
  >
    <div class="pa-4">
      <v-alert dense outlined type="info" class="text-caption mb-4">
        تغییرات قیمت بر روی تمام تنوع‌های انتخاب شده اعمال خواهد شد.
      </v-alert>

      <v-row dense>
        <!-- دکمه‌های نوع عملیات -->
        <v-col cols="12" class="mb-3">
          <label class="font-weight-bold text-caption d-block mb-1">نوع عملیات:</label>
          <v-btn-toggle
            v-model="form.operation"
            mandatory
            color="primary"
            background-color="white"
            class="d-flex w-100 rounded-lg flex-wrap"
          >
            <v-btn value="increase" class="flex-grow-1" outlined>
              <v-icon left small color="success">mdi-plus-circle</v-icon>
              افزایش قیمت
            </v-btn>
            <v-btn value="decrease" class="flex-grow-1" outlined>
              <v-icon left small color="error">mdi-minus-circle</v-icon>
              کاهش قیمت
            </v-btn>
            <v-btn value="set_range" class="flex-grow-1" outlined>
              <v-icon left small color="info">mdi-swap-horizontal</v-icon>
              تغییر قیمت (بازه)
            </v-btn>
          </v-btn-toggle>
        </v-col>

        <!-- حالت ۱: افزایش یا کاهش قیمت -->
        <template v-if="form.operation !== 'set_range'">
          <v-col cols="12" class="mb-3">
            <label class="font-weight-bold text-caption d-block mb-1">واحد محاسبه:</label>
            <v-radio-group v-model="form.type" row hide-details class="mt-0">
              <v-radio label="مبلغ ثابت (ریال)" value="fixed"></v-radio>
              <v-radio label="درصدی (%)" value="percent"></v-radio>
            </v-radio-group>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-if="form.type === 'percent'"
              v-model.number="form.value"
              label="میزان درصد کاهش/افزایش"
              outlined
              dense
              type="number"
              class="rounded-lg"
              suffix="درصد"
              :rules="[v => (v > 0 && v <= 100) || 'درصد باید بین ۱ تا ۱۰۰ باشد']"
              hide-details="auto"
            />
            <price-input
              v-else
              v-model="form.value"
              label="مبلغ تغییر (ریال)"
              dense
              :class="{ 'has-price-error': isDecreaseValueTooHigh }"
            />
          </v-col>

          <!-- خطای مجاز نبودن مبلغ کاهش بیش از حداقل قیمت -->
          <v-col v-if="isDecreaseValueTooHigh" cols="12" class="py-0 mt-1">
            <div class="error--text text-caption font-weight-bold d-flex align-center">
              <v-icon small color="error" class="ml-1">mdi-alert-circle-outline</v-icon>
              مبلغ کاهش نمی‌تواند بیشتر از حداقل قیمت فعلی تنوع‌ها ({{ formatPriceNumber(lowestAvailablePrice) }} ریال) باشد.
            </div>
          </v-col>
        </template>

        <!-- حالت ۲: تغییر حداقل و حداکثر قیمت -->
        <template v-else>
          <v-col cols="12" sm="6">
            <price-input
              v-model="form.min_price"
              label="حداقل قیمت"
              dense
              :class="{ 'has-price-error': isRangeInvalid }"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <price-input
              v-model="form.max_price"
              label="حداکثر قیمت"
              dense
              :class="{ 'has-price-error': isRangeInvalid }"
            />
          </v-col>
          <v-col v-if="isRangeInvalid" cols="12" class="py-0 mt-1">
            <div class="error--text text-caption font-weight-bold d-flex align-center">
              <v-icon small color="error" class="ml-1">mdi-alert-circle-outline</v-icon>
              حداقل قیمت باید کمتر از حداکثر قیمت باشد.
            </div>
          </v-col>
        </template>
      </v-row>

      <!-- دکمه‌ها -->
      <div class="d-flex align-center justify-end mt-6">
        <v-btn
          text
          class="btn-delete rounded-lg"
          @click="closeModal"
          :disabled="loading"
        >
          انصراف
        </v-btn>
        <v-spacer/>
        <v-btn
          class="btn-get-code rounded-lg"
          :loading="loading"
          :disabled="!isValid"
          @click="submitBulkPricing"
        >
          <v-icon left small>mdi-check</v-icon>
          اعمال تغییرات
        </v-btn>
      </div>
    </div>
  </custom-popup>
</template>

<script>
import priceInput from "../../../../core/plugins/TitleRibbon/pages/pickers/priceInput.vue";

export default {
  name: "BulkPriceModal",
  components: { priceInput },
  props: {
    value: { type: Boolean, default: false },
    selectedItems: { type: Array, default: () => [] }
  },
  data: () => ({
    loading: false,
    form: {
      operation: "increase", // 'increase' | 'decrease' | 'set_range'
      type: "percent",       // 'fixed' | 'percent'
      value: null,
      min_price: null,
      max_price: null
    }
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    numericMin() {
      return this.parseNumeric(this.form.min_price);
    },
    numericMax() {
      return this.parseNumeric(this.form.max_price);
    },
    numericValue() {
      return this.parseNumeric(this.form.value);
    },
    // پیدا کردن کمترین قیمت بین حداقل قیمت‌های آیتم‌های انتخاب‌شده
    lowestAvailablePrice() {
      if (!this.selectedItems || !this.selectedItems.length) return 0;
      const prices = this.selectedItems
        .map(item => this.parseNumeric(item.min_price))
        .filter(p => p > 0);

      return prices.length ? Math.min(...prices) : 0;
    },
    // بررسی اینکه آیا مبلغ کاهش ثابت بیشتر از کمترین حداقل قیمت است یا خیر
    isDecreaseValueTooHigh() {
      if (this.form.operation !== "decrease" || this.form.type !== "fixed") {
        return false;
      }
      if (this.lowestAvailablePrice > 0 && this.numericValue > this.lowestAvailablePrice) {
        return true;
      }
      return false;
    },
    isRangeInvalid() {
      if (this.form.operation !== "set_range") return false;
      if (this.numericMin > 0 && this.numericMax > 0) {
        return this.numericMin >= this.numericMax;
      }
      return false;
    },
    isValid() {
      if (this.form.operation === "set_range") {
        return this.numericMin > 0 && this.numericMax > 0 && !this.isRangeInvalid;
      }
      if (this.numericValue <= 0) return false;
      if (this.form.type === "percent" && this.numericValue > 100) return false;
      
      // جلوگیری از ثبت در صورت بیشتر بودن مبلغ کاهش از حداقل قیمت
      if (this.isDecreaseValueTooHigh) return false;

      return true;
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.syncInitialRange();
      }
    }
  },
  methods: {
    parseNumeric(val) {
      if (!val) return 0;
      const normalized = String(val)
        .replace(/[۰-۹]/g, d => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
        .replace(/[٠-٩]/g, d => "٠١٢٣٤٥٦٧٨٩".indexOf(d))
        .replace(/,/g, "")
        .trim();
      return Number(normalized) || 0;
    },
    formatPriceNumber(val) {
      if (!val) return "0";
      return Number(val).toLocaleString("fa-IR");
    },
    syncInitialRange() {
      if (!this.selectedItems || !this.selectedItems.length) {
        this.form.min_price = null;
        this.form.max_price = null;
        return;
      }

      const minList = this.selectedItems.map(item => this.parseNumeric(item.min_price));
      const maxList = this.selectedItems.map(item => this.parseNumeric(item.max_price));

      // اگر تمام حداقل قیمت‌ها یکسان بودند
      const allMinSame = minList.length > 0 && minList.every(v => v === minList[0]);
      this.form.min_price = allMinSame && minList[0] > 0 ? minList[0] : null;

      // اگر تمام حداکثر قیمت‌ها یکسان بودند
      const allMaxSame = maxList.length > 0 && maxList.every(v => v === maxList[0]);
      this.form.max_price = allMaxSame && maxList[0] > 0 ? maxList[0] : null;
    },
    closeModal() {
      this.dialog = false;
      this.form.value = null;
      this.form.min_price = null;
      this.form.max_price = null;
    },
    submitBulkPricing() {
      if (!this.isValid) return;

      this.$emit("apply", {
        operation: this.form.operation,
        type: this.form.type,
        value: this.numericValue,
        min_price: this.numericMin,
        max_price: this.numericMax
      });

      this.closeModal();
    }
  }
};
</script>

<style scoped>
::v-deep .has-price-error fieldset {
  border-color: #ff5252 !important;
  border-width: 2px !important;
}
</style>
